/* global $ module require*/
'use strict';

import { config } from '../tv_utils/global_config_data.js';

/**
 * Contains logic to handle searching.
 *
 * @property {string} term that was searched.
 * @property {HTMLElement} search_input_element is the input for searching.
 * @property {HTMLElement} search_output_element is the search drop-down containing search results.
 * @property {HTMLElement} search_button_element is the button to execute the search.
 * @property {number} timeout is the amount of time to wait before executing the search.
 * @property {number} min_characters_for_search the minimum amount of characters to search on.
 */
class Search {

    term: string = '';
    search_input_element: HTMLInputElement = null;
    search_output_element: HTMLElement = null;
    search_button_element: HTMLElement = null;
    mobile_search_opener: HTMLElement = null;
    mobile_search_closer: HTMLElement = null;
    mobile_search_bar: HTMLElement = null;
    timeout: NodeJS.Timer;
    min_characters_for_search: number;

    /**
     * Constructor, just sets to default values.
     */
    constructor() {
        this.min_characters_for_search = config().search.min_characters;
    }

    /**
     * Initializes the search javascript.
     *
     * @returns {void}
     * @memberof Search
     */
    init(): void {
        // Bind keyup on the search input so we can load results in an ajax overlay.
        this.search_input_element = <HTMLInputElement>document.getElementById('search_input');
        this.search_input_element.addEventListener('keyup', this.keyup.bind(this));

        // Bind the search results drop-down and if it's clicked, don't take any action.
        // This prevents the drop-down from closing itself.
        this.search_output_element = document.getElementById('tv-search-results');
        this.search_output_element.addEventListener('click', event => {
            event.stopPropagation();
        });

        // Bind the search button and perform the search when clicked.
        this.search_button_element = document.querySelector('.tv-search-button');
        this.search_button_element.addEventListener('click', this.search_full.bind(this));

        this.mobile_search_opener = document.querySelector('#js-search-expand-button');
        this.mobile_search_closer = document.querySelector('#js-mobile-search-close');
        this.mobile_search_bar = document.querySelector('#js-search-container');
        this.mobile_search_opener.addEventListener('click', this.open_mobile_search_bar.bind(this));
        this.mobile_search_closer.addEventListener('click', this.close_mobile_search_bar.bind(this));

        if (window.innerWidth < 767) {
            this.mobile_search_bar.classList.add('tv-display__none');
        }

        // If the body is clicked, close the search results drop-down.
        document.body.addEventListener('click', this.close.bind(this));
    }

    /**
     * Display the full search results page.
     *
     * @returns {void}
     */
    search_full(): void {
        window.location.assign('/index.php?dispatch=tv_search.search_full&term=' + this.term);
    }

    /**
     * Enable visibility of the mobile search bar
     * 
     * @returns {void}
     */
    open_mobile_search_bar(): void {
        this.mobile_search_bar.classList.add('tv-display__block');
        this.mobile_search_bar.classList.remove('tv-display__none');
    }

    /**
     * Disable visibility of the mobile search bar
     * 
     * @returns {void}
     */
    close_mobile_search_bar(): void {
        this.mobile_search_bar.classList.remove('tv-display__block');
        this.mobile_search_bar.classList.add('tv-display__none');
    }

    /**
     * Handles key ups on the search input.
     *
     * @param {Event} event The keyup event coming in from the DOM.
     * @returns {void}
     * @memberof Search
     */
    keyup(event): void {
        event.preventDefault();
        event.stopPropagation();

        this.term = this.search_input_element.value;

        // If the user hit enter, redirect to the full search results.
        if (event.keyCode === 13) {
            this.search_full();
        }

        // If it's under the minimum characters, don't do the ajax pop.
        if (this.term.length < this.min_characters_for_search) {
            return;
        }

        // If the user is typing and we need to clear a previous wait timeout, then do so here.
        if (this.timeout !== null) {
            clearTimeout(this.timeout);
        }

        // Set the half second timeout to wait until the user is done typing, hopefully.
        this.timeout = setTimeout(() => {
            this.search();
            this.timeout = null;
        }, 500);
    }

    /**
     * Called when the search input requirements are satisfied.
     *
     * @returns {void}
     * @memberof Search
     */
    search(): void {
        $.ceAjax('request', 'index.php?dispatch=tv_search.search', {
            result_ids: 'tv-search-results',
            method: 'POST',
            caching: false,
            data: { term: this.term },
            callback: () => {
                this.search_output_element.style.display = 'block';
            }
        });
    }

    /**
     * Handles the closing of the search results drop-down.
     *
     * @returns {void}
     * @memberof Search
     */
    close(): void {
        this.search_output_element.style.display = 'none';
    }
}

/**
 * Initialize the search interface javascript.
 *
 * @returns {void}
 */
export function init() {
    const search = new Search();
    search.init();
}
